import { Button, Chip, Link, makeStyles, Paper, TextField, Tooltip, Typography } from "@material-ui/core";
import { Alert, Autocomplete, Skeleton } from "@material-ui/lab";
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { MdCheck, MdDeleteOutline, MdHistory, MdVerifiedUser, MdVpnKey } from "react-icons/md";
import { Link as RouterLink, Prompt, Redirect, useHistory, useParams } from "react-router-dom";
import { CREATE_NEW_ITEM } from "../../../../constants/constants";
import { useQuery } from "../../../../hooks/useQuery";
import { planEatApi } from "../../../../planEatApi/PlanEatApi";
import { AdminFor, buildAccounts, GROUPS, groupLabel } from "../../accounts";
import { EaterGroupSearch } from "../../components/EaterGroupSearch";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { SaveBar, Section } from "../../components/EntityDetailLayout";
import { PageHeader } from "../../components/PageHeader";
import { sectionLabels } from "../../components/VersionsPage";
import { useSysadminData } from "../../SysadminData";
import { errorMessage, safeDecode, sysadminPaths } from "../../sysadminUtils";
import { StatusChip } from "./Users";
import { DERIVED_GROUP, initialForm, MANUAL_GROUPS, resultingGroups, runSteps, saveSteps, UserForm } from "./saveAccount";

const useStyles = makeStyles(theme => ({
    chips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    banner: {
        marginBottom: theme.spacing(3),
    },
    resulting: {
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    option: {
        display: "flex",
        flexDirection: "column",
    },
    deleteButton: {
        color: theme.palette.error.main,
    },
    section: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    panelHeader: {
        marginBottom: theme.spacing(2),
    },
    panelTitleRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1),
    },
    panelTitle: {
        fontWeight: 700,
        wordBreak: "break-all",
        flexGrow: 1,
        marginTop: theme.spacing(0.5),
    },
    panelControls: {
        display: "flex",
        flexShrink: 0,
    },
    panelActions: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
    },
}));

/** Sends the user an email with a link to choose a (new) password. */
export const SendResetLinkButton: React.FC<{ email: string; disabled?: boolean; size?: "small" | "medium" }> = ({ email, disabled, size }) => {
    const [open, setOpen] = useState(false);

    async function send() {
        try {
            await planEatApi.postForgotPassword(email);
            toast.success(`Reset link sent to ${email}`);
            setOpen(false);
        } catch (e) {
            toast.error(errorMessage(e, "Could not send the reset link"));
        }
    }

    return (
        <>
            <Button variant="outlined" size={size} startIcon={<MdVpnKey />} disabled={disabled} onClick={() => setOpen(true)}>
                Send reset link
            </Button>
            <ConfirmDialog
                open={open}
                title="Send password reset link?"
                message={<p>An email with a link to choose a new password is sent to <b>{email}</b>. The link is the same as when using "Forgot password" on the login page.</p>}
                confirmLabel="Send"
                onConfirm={send}
                onClose={() => setOpen(false)}
            />
        </>
    );
};

const groupDescriptions: Record<string, string> = {
    [GROUPS.eater]: "Can book meals for their eater group.",
    [GROUPS.kitchenManager]: "Works in a kitchen and can see bookings for the office.",
    [GROUPS.systemAdmin]: "Full access to this sysadmin page. Only give this to Datasolvr / PlanEat staff.",
    [GROUPS.unverified]: "The email address is not verified yet. Unverified users only see the verification page.",
};


const byName = <T,>(items: T[], name: (item: T) => string) => [...items].sort((a, b) => name(a).localeCompare(name(b)));

export interface Prefill {
    officeId: string;
    eaterGroupId: string;
}

/** The users page with the user given in the URL. */
export const UserAccount: React.FC = () => {
    const params = useParams<{ userId: string }>();
    const email = safeDecode(params.userId);
    const data = useSysadminData();
    const history = useHistory();
    const query = useQuery();

    if (email === CREATE_NEW_ITEM) {
        return <Redirect to={sysadminPaths.list("users")} />;
    }
    // Old links use the PlanEat user id
    const byPlanEatId = !data.loading && !data.serviceUsers.some(s => s.content.id === email) ? data.users.find(u => u.content.id === email) : undefined;
    if (byPlanEatId) {
        return <Redirect to={sysadminPaths.detail("users", byPlanEatId.content.applicationUserId)} />;
    }
    const officeId = query.get("officeId");
    return (
        <UserEditor
            email={email}
            variant="page"
            prefill={officeId ? { officeId, eaterGroupId: query.get("eaterGroupId") ?? "" } : undefined}
            onDeleted={() => history.push(sysadminPaths.list("users"))}
        />
    );
};

interface EditorProps {
    email: string;
    variant: "page" | "panel";
    /** Placement suggested when the form is first shown, e.g. when adding a user to an eater group. */
    prefill?: Prefill;
    /** Panel only: navigation and close buttons shown next to the title. */
    panelControls?: React.ReactNode;
    onDeleted: () => void;
}

/** View and edit a login account: placement, user groups and what the user administrates. */
export const UserEditor: React.FC<EditorProps> = ({ email, variant, prefill, panelControls, onDeleted }) => {
    const classes = useStyles();
    const data = useSysadminData();
    const history = useHistory();
    const isPanel = variant === "panel";

    const accounts = useMemo(() => buildAccounts(data), [data]);
    const account = accounts.find(a => a.email === email);

    const [form, setForm] = useState<UserForm>();
    const [original, setOriginal] = useState<UserForm>();
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string>();
    const [confirmDelete, setConfirmDelete] = useState(false);
    const allowNavigation = useRef(false);
    const forceReset = useRef(false);

    const dirty = !!form && !!original && JSON.stringify(form) !== JSON.stringify(original);

    // (Re)initialise the form when data is (re)loaded, unless the sysadmin has unsaved changes
    useEffect(() => {
        if (data.loading || !account) return;
        if (dirty && !forceReset.current) return;
        const init = initialForm(account, data);
        setOriginal(init);
        if (!original && prefill) {
            setForm({ ...init, officeId: prefill.officeId, companyId: data.companyIdOfOffice(prefill.officeId) ?? "", eaterGroupId: prefill.eaterGroupId });
        } else {
            setForm(init);
        }
        forceReset.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, data.loading]);

    const crumbs = [{ label: sectionLabels.users, to: sysadminPaths.list("users") }, { label: email }];

    const header = (subtitle?: React.ReactNode, actions?: React.ReactNode) => isPanel ? (
        <div className={classes.panelHeader}>
            <div className={classes.panelTitleRow}>
                <Typography variant="h6" component="h2" className={classes.panelTitle}>{email}</Typography>
                <div className={classes.panelControls}>{panelControls}</div>
            </div>
            {subtitle}
            {actions && <div className={classes.panelActions}>{actions}</div>}
        </div>
    ) : (
        <PageHeader title={email} breadcrumbs={crumbs} subtitle={subtitle} actions={actions} />
    );

    if (!account || !form || !original) {
        return (
            <div>
                {header()}
                {data.loading || account ? (
                    <Paper variant="outlined" className={classes.section}>
                        <Skeleton height={40} />
                        <Skeleton height={40} />
                        <Skeleton height={40} width="60%" />
                    </Paper>
                ) : (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={data.reload}>Retry</Button>}>
                        {data.error ?? `No user with the email ${email} was found`}
                    </Alert>
                )}
            </div>
        );
    }

    const readOnly = !account.serviceUser;
    const update = (changes: Partial<UserForm>) => setForm({ ...form, ...changes });
    const toggleGroup = (group: string, on: boolean) => update({ groups: on ? [...form.groups, group] : form.groups.filter(g => g !== group) });


    const entityName = (kind: keyof AdminFor, id: string) =>
        kind === "companies" ? data.companyById(id)?.companyName || "Unnamed company"
            : kind === "offices" ? data.officeById(id)?.officeName || "Unnamed office"
                : data.eaterGroupById(id)?.eaterGroupName || "Unnamed eater group";
    const adminsOf = (kind: keyof AdminFor, id: string) =>
        (kind === "companies" ? data.companyById(id)?.companyAdminIds
            : kind === "offices" ? data.officeById(id)?.officeAdminIds
                : data.eaterGroupById(id)?.eaterGroupAdminIds) ?? [];

    const missing: string[] = [];
    if (form.officeId && !form.eaterGroupId) missing.push("eater group");
    (Object.keys(DERIVED_GROUP) as (keyof AdminFor)[]).forEach(kind => {
        original.adminFor[kind]
            .filter(id => !form.adminFor[kind].includes(id) && adminsOf(kind, id).filter(a => a !== email).length === 0)
            .forEach(id => missing.push(`another admin for ${entityName(kind, id)} (it needs at least one)`));
    });

    const groupsAfterSave = resultingGroups(account.groups, original, form);
    const canSave = !readOnly && (dirty || account.groupsNeedRepair);

    async function save() {
        setSaving(true);
        setSaveError(undefined);
        allowNavigation.current = true;
        const result = await runSteps(saveSteps(account!, original!, form!, entityName));
        if (result.failed) {
            setSaveError(`Could not ${result.failed.label}: ${result.failed.message}` +
                (result.done.length > 0 ? `. Already saved: ${result.done.join(", ")}.` : "") +
                " Your changes are kept, so you can correct them and save again.");
            toast.error("Not all changes were saved");
        } else {
            forceReset.current = true;
            toast.success("User saved");
        }
        await data.reload();
        allowNavigation.current = false;
        setSaving(false);
    }

    async function remove() {
        allowNavigation.current = true;
        try {
            if (account!.user) {
                // Also deletes the login account
                await planEatApi.postDeleteForUser(account!.user.id);
            } else {
                await planEatApi.postDeleteForServiceUser(account!.email);
            }
            toast.success("User deleted");
            await data.reload();
            onDeleted();
        } catch (e) {
            allowNavigation.current = false;
            toast.error(errorMessage(e, "Could not delete user"));
            setConfirmDelete(false);
        }
    }

    const markVerified = () => update({ groups: [...form.groups.filter(g => g !== GROUPS.unverified && g !== GROUPS.eater), GROUPS.eater] });
    const adminCount = account.adminFor.companies.length + account.adminFor.offices.length + account.adminFor.eaterGroups.length;
    const eaterGroupMembers = account.user?.eaterGroupId ? data.users.filter(u => u.content.eaterGroupId === account.user!.eaterGroupId).length : 0;

    const groupsChanged = JSON.stringify([...groupsAfterSave].sort()) !== JSON.stringify([...account.groups].sort());

    // One picker for all admin roles; options are encoded as "<kind>:<id>"
    const kinds: (keyof AdminFor)[] = ["companies", "offices", "eaterGroups"];
    const kindLabels: Record<keyof AdminFor, string> = { companies: "Companies", offices: "Offices (kitchens)", eaterGroups: "Eater groups" };
    const parseKey = (key: string) => {
        const index = key.indexOf(":");
        return { kind: key.slice(0, index) as keyof AdminFor, id: key.slice(index + 1) };
    };
    const companyOf = (kind: keyof AdminFor, id: string) =>
        kind === "companies" ? id : kind === "offices" ? data.companyIdOfOffice(id) : data.companyIdOfEaterGroup(id);
    const describe = (key: string) => {
        const { kind, id } = parseKey(key);
        if (kind === "companies") return "";
        const officeId = kind === "offices" ? id : data.eaterGroupById(id)?.officeId;
        return [kind === "eaterGroups" ? data.officeById(officeId)?.officeName : undefined, data.companyById(companyOf(kind, id))?.companyName]
            .filter(Boolean).join(" · ");
    };
    const adminValue = kinds.flatMap(kind => form.adminFor[kind].map(id => `${kind}:${id}`));
    const allIds: Record<keyof AdminFor, string[]> = {
        companies: data.companies.map(c => c.content.id),
        offices: data.offices.map(o => o.content.id),
        eaterGroups: data.eaterGroups.map(g => g.content.id),
    };
    const adminOptions = kinds.flatMap(kind => byName(
        // Only the selected company's entities, but never hide a current role
        allIds[kind].filter(id => data.matchesSelectedCompany(companyOf(kind, id)) || form.adminFor[kind].includes(id)),
        id => `${describe(`${kind}:${id}`)} ${entityName(kind, id)}`
    ).map(id => `${kind}:${id}`));
    const setAdminKeys = (keys: string[]) => update({
        adminFor: {
            companies: keys.map(parseKey).filter(k => k.kind === "companies").map(k => k.id),
            offices: keys.map(parseKey).filter(k => k.kind === "offices").map(k => k.id),
            eaterGroups: keys.map(parseKey).filter(k => k.kind === "eaterGroups").map(k => k.id),
        },
    });

    return (
        <div>
            <Prompt when={dirty} message={() => allowNavigation.current || "You have unsaved changes. Leave the page anyway?"} />
            {header(
                <span className={classes.chips} style={{ marginTop: 4 }}><StatusChip status={account.status} />{account.name && <span>{account.name}</span>}</span>,
                <>
                    <SendResetLinkButton email={email} disabled={readOnly} size={isPanel ? "small" : "medium"} />
                    {account.user && (
                        <Button variant="outlined" size={isPanel ? "small" : "medium"} startIcon={<MdHistory />} onClick={() => history.push(sysadminPaths.versions("users", email))}>
                            Placement history
                        </Button>
                    )}
                    <Button className={classes.deleteButton} size={isPanel ? "small" : "medium"} startIcon={<MdDeleteOutline />} onClick={() => setConfirmDelete(true)}>
                        Delete
                    </Button>
                </>
            )}

            {readOnly && (
                <Alert severity="error" className={classes.banner}>
                    The login account for this email no longer exists, so the user cannot log in or be changed. Delete the user to clean up.
                </Alert>
            )}
            {!readOnly && form.groups.includes(GROUPS.unverified) && (
                <Alert severity="warning" className={classes.banner}
                    action={<Button color="inherit" size="small" startIcon={<MdVerifiedUser />} onClick={markVerified}>Mark as verified</Button>}>
                    This user has not verified their email address yet.
                </Alert>
            )}
            {!readOnly && account.groupsNeedRepair && (
                <Alert severity="warning" className={classes.banner}>
                    The user groups are stored in a broken format ({JSON.stringify(account.serviceUser?.content.data_raw?.groups)}). Saving repairs them.
                </Alert>
            )}
            {saveError && <Alert severity="error" className={classes.banner} onClose={() => setSaveError(undefined)}>{saveError}</Alert>}

            <Section dense={isPanel} title="Placement" description="The eater group the user books meals for.">
                {!account.user && (
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                        The user is not placed anywhere yet.
                    </Typography>
                )}
                <EaterGroupSearch
                    value={form.eaterGroupId}
                    disabled={readOnly}
                    // A placed user can be moved but not unplaced
                    clearable={!account.user}
                    onChange={placement => update(placement)}
                    error={!!form.eaterGroupId && !data.eaterGroupById(form.eaterGroupId)}
                    helperText={form.eaterGroupId && !data.eaterGroupById(form.eaterGroupId) ? "The current eater group no longer exists. Choose another one." : undefined}
                />
                {form.eaterGroupId && !form.groups.includes(GROUPS.eater) && !readOnly && (
                    <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
                        The user is placed in an eater group but is not an eater, so they cannot book meals.{" "}
                        <Link component="button" variant="body2" onClick={() => toggleGroup(GROUPS.eater, true)}>Make eater</Link>
                    </Typography>
                )}
            </Section>

            <Section dense={isPanel} title="User groups" description="Click to turn a group on or off. Hover for what it allows.">
                <div className={classes.chips}>
                    {MANUAL_GROUPS.map(group => {
                        const on = form.groups.includes(group);
                        return (
                            <Tooltip key={group} title={groupDescriptions[group]}>
                                <Chip
                                    label={groupLabel(group)}
                                    icon={on ? <MdCheck /> : undefined}
                                    color={on ? "primary" : "default"}
                                    variant={on ? "default" : "outlined"}
                                    disabled={readOnly}
                                    clickable
                                    onClick={() => toggleGroup(group, !on)}
                                    aria-pressed={on}
                                />
                            </Tooltip>
                        );
                    })}
                </div>
                {form.groups.includes(GROUPS.systemAdmin) && (
                    <Typography variant="body2" color="error" style={{ marginTop: 8 }}>{groupDescriptions[GROUPS.systemAdmin]}</Typography>
                )}
                {(groupsChanged || groupsAfterSave.length === 0) && (
                    <div className={classes.resulting}>
                        <Typography variant="caption" color="textSecondary" component="div" gutterBottom>
                            User groups after saving, including manager groups from "Admin for":
                        </Typography>
                        <div className={classes.chips}>
                            {groupsAfterSave.length === 0
                                ? <Typography variant="body2" color="error">No groups — the user cannot do anything</Typography>
                                : groupsAfterSave.map(group => (
                                    <Chip key={group} size="small" label={groupLabel(group)}
                                        color={account.groups.includes(group) ? "default" : "primary"}
                                        title={group} />
                                ))}
                            {account.groups.filter(g => !groupsAfterSave.includes(g)).map(group => (
                                <Chip key={group} size="small" label={groupLabel(group)} style={{ textDecoration: "line-through", opacity: 0.6 }} title={`${group} will be removed`} />
                            ))}
                        </div>
                    </div>
                )}
            </Section>

            <Section dense={isPanel} title="Admin for" description="Admins manage the chosen companies, offices and eater groups. The matching manager group is added or removed automatically.">
                <Autocomplete
                    multiple
                    filterSelectedOptions
                    disabled={readOnly}
                    options={adminOptions}
                    value={adminValue}
                    onChange={(_, keys) => setAdminKeys(keys as string[])}
                    groupBy={key => kindLabels[parseKey(key).kind]}
                    getOptionLabel={key => entityName(parseKey(key).kind, parseKey(key).id)}
                    renderOption={key => (
                        <div className={classes.option}>
                            <span>{entityName(parseKey(key).kind, parseKey(key).id)}</span>
                            {describe(key) && <Typography variant="caption" color="textSecondary">{describe(key)}</Typography>}
                        </div>
                    )}
                    renderTags={(keys, getTagProps) => keys.map((key, index) => {
                        const { kind, id } = parseKey(key);
                        return (
                            <Tooltip key={key} title={[kindLabels[kind].replace(/s$/, ""), describe(key)].filter(Boolean).join(" · ")}>
                                <Chip
                                    size="small"
                                    label={entityName(kind, id)}
                                    color={original.adminFor[kind].includes(id) ? "default" : "primary"}
                                    {...getTagProps({ index })}
                                />
                            </Tooltip>
                        );
                    })}
                    renderInput={params => (
                        <TextField {...params} variant="outlined" size="small" placeholder={adminValue.length ? "" : "Search company, office or eater group"} />
                    )}
                />
                {account.user?.eaterGroupId && (
                    <Typography variant="body2" style={{ marginTop: 12 }}>
                        <Link component={RouterLink} to={sysadminPaths.detail("eaterGroups", account.user.eaterGroupId)}>Open the user's eater group</Link>
                    </Typography>
                )}
            </Section>

            {canSave && (
                <SaveBar
                    missing={missing}
                    info={dirty ? "Unsaved changes" : "Save to repair user groups"}
                    saving={saving}
                    canSave={canSave}
                    saveLabel="Save changes"
                    onCancel={() => setForm(original)}
                    onSave={save}
                />
            )}

            <ConfirmDialog
                open={confirmDelete}
                title="Delete user?"
                message={
                    <>
                        <p><b>{email}</b> and their login account will be deleted.</p>
                        {adminCount > 0 && <p>The user is admin for {adminCount} compan(y/ies), office(s) or eater group(s). Remove them under "Admin for" first, so those are not left with a deleted admin.</p>}
                        {eaterGroupMembers === 1 && <p>The user is the last member of their eater group, which is then <b>also deleted</b>.</p>}
                    </>
                }
                confirmLabel="Delete"
                destructive
                onConfirm={remove}
                onClose={() => setConfirmDelete(false)}
            />
        </div>
    );
};
